import React, { cloneElement, useRef, useState } from 'react';
import classNames from 'classnames';
import { Transition } from 'react-transition-group';
import {
  omit,
  pick,
  TransitionPropTypeKeys,
  TransitionStatuses,
  TransitionTimeouts,
} from './utils';

const transitionStatusToClassHash = {
  [TransitionStatuses.ENTERING]: 'collapsing',
  [TransitionStatuses.ENTERED]: 'collapse show',
  [TransitionStatuses.EXITING]: 'collapsing',
  [TransitionStatuses.EXITED]: 'collapse',
};

const getTransitionClass = (status) => {
  return transitionStatusToClassHash[status] || 'collapse';
};

const getHeight = (node) => {
  return node ? node.scrollHeight : null;
};

const Collapse = ({
  tag: Tag = 'div',
  isOpen,
  className,
  isNavbar,
  children,
  isChildClone,
  ...restProps
}) => {
  const props = {
    ...restProps,
    ...Transition.defaultProps,
    timeout: restProps.timeout ?? TransitionTimeouts.Collapse,
  };
  const ref = useRef(null);
  const NODE = ref.current;

  const [height, setHeight] = useState(null);

  const onEntering = (isAppearing) => {
    setHeight(getHeight(NODE));
    if (props.onEntering) props.onEntering(NODE, isAppearing);
  };

  const onEntered = (isAppearing) => {
    setHeight(null);
    if (props.onEntered) props.onEntered(NODE, isAppearing);
  };

  const onExit = () => {
    setHeight(getHeight(NODE));
    if (props.onExit) props.onExit(NODE);
  };

  const onExiting = () => {
    if (NODE) {
      // accessing offsetHeight forces reflow
      const UNUSED = NODE.offsetHeight; // eslint-disable-line no-unused-vars
    }
    setHeight(0);
    if (props.onExiting) props.onExiting(NODE);
  };

  const onExited = () => {
    setHeight(null);
    if (props.onExited) props.onExited(NODE);
  };

  const transitionProps = pick(props, TransitionPropTypeKeys);
  const childProps = omit(props, TransitionPropTypeKeys);

  return (
    <Transition
      nodeRef={ref}
      {...transitionProps}
      in={isOpen}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      onExiting={onExiting}
      onExited={onExited}
    >
      {(status) => {
        const collapseClass = getTransitionClass(status);
        const classes = classNames(className, collapseClass, isNavbar && 'navbar-collapse');
        const style = height === null ? null : { height };
        if (isChildClone) {
          return cloneElement(children, {
            ref,
            style: { ...childProps.style, ...style },
            className: classNames(classes, children.props.className),
            ...childProps,
          });
        }
        return (
          <Tag
            {...childProps}
            style={{ ...childProps.style, ...style }}
            className={classes}
            ref={ref}
          >
            {children}
          </Tag>
        );
      }}
    </Transition>
  );
};

export default Collapse;
